<template>
  <div class="async-file-upload">
    <!-- Upload Area -->
    <div
      class="file-upload-area"
      :class="{
        'has-files': files.length > 0,
        'is-invalid': hasError,
        'is-uploading': isUploading,
      }"
      @click="triggerFileInput"
      @dragover.prevent
      @drop.prevent="handleFileDrop"
    >
      <div class="file-upload-content">
        <div class="upload-icon-container">
          <font-awesome-icon
            :icon="isUploading ? 'spinner' : 'cloud-upload-alt'"
            class="upload-icon"
            :class="{ 'fa-spin': isUploading }"
          />
        </div>
        <p class="upload-text">
          {{ getUploadText() }}
        </p>
        <p class="upload-hint">
          <strong>Unterstützte Formate:</strong> PDF (max. 10 MB), JPEG, PNG, GIF, HEIC, HEIF<br />
          <small class="text-muted">Bilder werden automatisch komprimiert und optimiert</small>
        </p>
      </div>
    </div>

    <input
      type="file"
      :id="`file-input-${fieldName}`"
      :ref="`fileInput-${fieldName}`"
      multiple
      accept=".pdf,.jpg,.jpeg,.png,.gif,.heic,.heif"
      @change="handleFileSelect"
      style="display: none"
    />

    <!-- File List -->
    <div v-if="files.length > 0" class="mt-3">
      <div class="file-list-card">
        <div class="file-list-header">
          <h6 class="mb-0">
            <font-awesome-icon icon="file-upload" class="me-2" />
            Hochgeladene Dateien
          </h6>
        </div>
        <div class="file-list-body">
          <div v-for="(file, index) in files" :key="index" class="file-item">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center flex-grow-1">
                <div class="file-icon-container">
                  <font-awesome-icon :icon="getFileIcon(file.type)" class="file-icon" />
                </div>
                <div class="file-info">
                  <span class="file-name">{{
                    file.originalName || file.name || `Datei ${index + 1}`
                  }}</span>
                  <span v-if="file.size" class="file-size">
                    ({{ formatFileSize(file.size) }})
                  </span>
                </div>
              </div>
              <button
                type="button"
                class="btn btn-sm btn-outline-danger file-remove-btn ms-2"
                @click="removeFile(index)"
                title="Datei löschen"
              >
                <font-awesome-icon icon="trash" class="small" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="alert alert-danger mt-2">
      {{ errorMessage }}
    </div>

    <!-- Required Field Message -->
    <div v-if="props.required && files.length === 0 && !isUploading" class="invalid-feedback d-block mt-2">
      <font-awesome-icon icon="exclamation-triangle" class="me-1" />
      Dieses Feld ist erforderlich. Bitte lade mindestens eine Datei hoch.
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { receiptsApi } from '@/api/receipts'
import { showError, showConfirmDialog } from '@/services/notification'

const props = defineProps({
  fieldName: {
    type: String,
    required: true,
  },
  sessionId: {
    type: String,
    required: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:files', 'validation-change', 'session-created'])

const files = ref([])
const isUploading = ref(false)
const errorMessage = ref('')
const sessionId = ref(props.sessionId || null)


const loadFilesFromSession = async () => {
  if (sessionId.value) {
    try {
      const response = await receiptsApi.getUploadSessionStatus(sessionId.value)
      if (response.status && response.status.files) {
        files.value = response.status.files
        emit('update:files', files.value)
        emit('validation-change', !hasError.value)
      }
    } catch (error) {
      console.error('Error loading files from session:', error)
      
      files.value = []
      emit('update:files', files.value)
      emit('validation-change', !hasError.value)
    }
  }
}

const hasError = computed(() => {
  return errorMessage.value || (props.required && files.value.length === 0)
})

const getUploadText = () => {
  if (isUploading.value) {
    return 'Dateien werden verarbeitet...'
  } else if (files.value.length > 0) {
    return `${files.value.length} Datei${files.value.length > 1 ? 'en' : ''} ausgewählt`
  } else {
    return 'Dateien hier hineinziehen oder klicken zum Auswählen'
  }
}

const triggerFileInput = () => {
  if (isUploading.value) return

  const fileInput = document.getElementById(`file-input-${props.fieldName}`)
  if (fileInput) {
    fileInput.click()
  }
}

const handleFileSelect = (event) => {
  try {
    const selectedFiles = Array.from(event.target.files)
    addFiles(selectedFiles)
    event.target.value = '' 
  } catch (error) {
    console.error('Error in handleFileSelect:', error)
    showError('Fehler beim Auswählen der Dateien')
  }
}

const handleFileDrop = (event) => {
  try {
    if (isUploading.value) return

    const droppedFiles = Array.from(event.dataTransfer.files)
    addFiles(droppedFiles)
  } catch (error) {
    console.error('Error in handleFileDrop:', error)
    showError('Fehler beim Hochladen der Dateien')
  }
}

const addFiles = async (newFiles) => {
  try {
    if (isUploading.value) return

    
    const validFiles = newFiles.filter((file) => {
      const validTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'application/pdf',
        'image/heic',
        'image/heif',
      ]

      if (!validTypes.includes(file.type)) {
        showError(
          `Datei ${file.name} hat einen ungültigen Typ. Erlaubt sind: PDF, JPEG, PNG, GIF, HEIC, HEIF`,
        )
        return false
      }

      
      if (file.type === 'application/pdf') {
        const maxSize = 10 * 1024 * 1024 
        if (file.size > maxSize) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2)
          showError(`PDF-Datei ${file.name} ist zu groß (${fileSizeMB} MB). Maximale Größe: 10 MB`)
          return false
        }
      }

      return true
    })

    if (validFiles.length === 0) return

    
    if (!sessionId.value) {
      try {
        const session = await receiptsApi.createUploadSession()
        sessionId.value = session.sessionId
        emit('session-created', sessionId.value)
      } catch (error) {
        console.error('Error creating upload session:', error)
        showError('Fehler beim Erstellen der Upload-Session')
        return
      }
    }

    
    const fileInfos = validFiles.map((file) => ({
      id: null,
      originalName: file.name,
      size: file.size,
      type: file.type,
      status: 'pending',
      progress: 0,
      error: null,
      statusMessage: null,
    }))

    files.value.push(...fileInfos)
    emit('update:files', files.value)
    emit('validation-change', !hasError.value)

    
    await uploadFiles(validFiles, fileInfos)
  } catch (error) {
    console.error('Error in addFiles:', error)
    showError('Fehler beim Hinzufügen der Dateien')
  }
}

const uploadFiles = async (fileObjects, fileInfos) => {
  isUploading.value = true
  errorMessage.value = ''

  try {
    for (let i = 0; i < fileObjects.length; i++) {
      const file = fileObjects[i]
      const fileInfo = fileInfos[i]

      try {
        const result = await receiptsApi.addFileToSession(sessionId.value, file)
        fileInfo.id = result.fileId
        fileInfo.status = 'completed'
      } catch (error) {
        fileInfo.status = 'failed'
        fileInfo.error = error.response?.data?.error || 'Upload fehlgeschlagen'
      }
    }
  } catch (error) {
    errorMessage.value = 'Fehler beim Hochladen der Dateien'
    console.error('Upload error:', error)
  } finally {
    isUploading.value = false
  }
}


const getFileIcon = (fileType) => {
  if (!fileType) return 'file'

  if (fileType.startsWith('image/')) return 'file-image'
  if (fileType.startsWith('video/')) return 'file-video'
  if (fileType.startsWith('audio/')) return 'file-audio'
  if (fileType.includes('pdf')) return 'file-pdf'
  if (fileType.includes('word') || fileType.includes('document')) return 'file-word'
  if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'file-excel'
  if (fileType.includes('zip') || fileType.includes('archive')) return 'file-archive'

  return 'file'
}

const formatFileSize = (bytes) => {
  if (!bytes) return ''

  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  if (bytes === 0) return '0 Bytes'

  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i]
}

const getFileTypeName = (mimeType) => {
  if (!mimeType) return 'Unbekannt'

  const typeMap = {
    'image/jpeg': 'JPEG Bild',
    'image/jpg': 'JPEG Bild',
    'image/png': 'PNG Bild',
    'image/gif': 'GIF Bild',
    'image/heic': 'HEIC Bild',
    'image/heif': 'HEIF Bild',
    'application/pdf': 'PDF Dokument',
    'image/webp': 'WebP Bild',
    'image/bmp': 'BMP Bild',
    'image/tiff': 'TIFF Bild',
    'image/svg+xml': 'SVG Bild',
  }

  return typeMap[mimeType] || mimeType
}

const removeFile = async (index) => {
  try {
    const file = files.value[index]

    if (!file) {
      console.warn(`File not found at index ${index}`)
      return
    }

    
    const result = await showConfirmDialog(
      'Datei löschen',
      `Möchtest du die Datei "${file.originalName || file.name}" wirklich löschen?`,
      'Löschen',
      'Abbrechen',
      {
        icon: 'warning',
        confirmButtonColor: '#dc3545',
      },
    )

    if (!result.isConfirmed) {
      return
    }

    
    if (file.id && sessionId.value) {
      await receiptsApi.removeFileFromSession(sessionId.value, file.id)
    }

    
    files.value.splice(index, 1)
    emit('update:files', files.value)
    emit('validation-change', !hasError.value)
  } catch (error) {
    console.error('Error in removeFile:', error)
    showError('Fehler beim Löschen der Datei')
  }
}


const getSessionId = () => sessionId.value


defineExpose({
  getSessionId,
  files,
})

onMounted(() => {
  loadFilesFromSession()
})


watch(
  () => props.sessionId,
  (newSessionId) => {
    if (newSessionId && newSessionId !== sessionId.value) {
      sessionId.value = newSessionId
      loadFilesFromSession()
    }
  },
)
</script>

